"use client";

// ── MobileNavSafeArea — the document reserves the room the fixed bottom bar takes ────────────────
//
// THE DEFECT, measured on the served build at 390x844 and reproduced IDENTICALLY on deployed
// staging, so it is pre-existing rather than a regression: the footer's legal row renders at y=768
// with height 14, and the design's fixed bottom navigation occupies y=768 to 834. A hit test at the
// legal row returns the nav BUTTON, not the text — so the copyright line and the Privacy / Terms /
// Contact / FAQ links sit underneath the bar and cannot be read or tapped, at any scroll position,
// because the bar is fixed and the document simply ends behind it.
//
// WHY THIS LIVES HERE AND NOT IN THE FOOTER. Two attempts inside the design export's own footer were
// made and reverted (2026-09-02): a binding placed inside the `padding` SHORTHAND is dropped by the
// design runtime — the rendered style came back with no third value and the bottom padding silently
// HALVED from 36px to 18px — and moving it to its own `padding-bottom` declaration resolved to 0px,
// because the producer key never reached that template's scope. Founder ruling after those two: the
// reserve belongs to whatever owns the fixed navigation, not to the generated footer, and the
// export's footer padding is not to be touched again. This layer therefore changes nothing in the
// design file; it adds space AFTER the design's own content, on the host element that carries it.
//
// MEASURED, NOT ASSUMED. The bar's height is read from the bar itself every time, so a bar that
// changes height, or a surface that renders none, cannot leave a stale reserve behind. `innerHeight
// - top` is used rather than the bar's own height because the bar carries its own inset (measured:
// height 66, bottom 834 on an 844 viewport, i.e. a 10px gap below it) and the reserve has to clear
// the whole occupied strip, not just the painted box. `env(safe-area-inset-bottom)` is added on top
// for the device's own home-indicator inset; it resolves to 0px where there is none, which is why
// the fallback is written explicitly.
//
// IDEMPOTENT BY CONSTRUCTION. The original inline value is stashed on the host the first time and
// restored when the bar goes away, so a re-run can never stack a second reserve on the first, and a
// viewport that loses the bar (a phone rotated to a tablet width, or a desktop render) goes back to
// exactly the padding it had. That is the "no double spacing when the mobile navigation is absent"
// half of the requirement, and it is why this is an attribute-guarded swap rather than an append.

import { useEffect } from "react";

/** Where the pre-existing inline padding is stashed while a reserve is in place. */
const MARK = "data-sg-nav-safe-area";

/** The bar is found by WHAT IT IS, never by a class name the generator can rewrite: fixed, spanning
 *  most of the width, bar-shaped, and pinned to the bottom of the viewport. */
function findBottomBar(): HTMLElement | null {
  for (const el of Array.from(document.querySelectorAll<HTMLElement>("nav, aside, div, footer"))) {
    const cs = getComputedStyle(el);
    if (cs.position !== "fixed") continue;
    if (cs.visibility === "hidden" || cs.display === "none") continue;
    const r = el.getBoundingClientRect();
    if (r.width < window.innerWidth * 0.6) continue;
    if (r.height < 32 || r.height > 140) continue;
    if (window.innerHeight - r.bottom > 40) continue;
    return el;
  }
  return null;
}

export default function MobileNavSafeArea() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    let disposed = false;

    const apply = () => {
      if (disposed) return;
      const host = document.querySelector<HTMLElement>("[data-sg-design-surface]");
      if (!host) return;
      const bar = findBottomBar();
      if (!bar) {
        if (host.hasAttribute(MARK)) {
          host.style.paddingBottom = host.getAttribute(MARK) || "";
          host.removeAttribute(MARK);
        }
        return;
      }
      const reserve = Math.round(window.innerHeight - bar.getBoundingClientRect().top);
      if (reserve <= 0) return;
      const want = `calc(${reserve}px + env(safe-area-inset-bottom, 0px))`;
      if (!host.hasAttribute(MARK)) host.setAttribute(MARK, host.style.paddingBottom || "");
      if (host.style.paddingBottom !== want) host.style.paddingBottom = want;
    };

    // The design surface is fetched and injected after mount, and the export swaps whole screens as
    // the visitor navigates, so a one-shot pass would fix the first screen and nothing after it —
    // the same trap CorridorBasemapLayer records. Observe, and re-measure when the tree changes.
    apply();
    const observer = new MutationObserver(() => apply());
    observer.observe(document.body, { childList: true, subtree: true });
    const onResize = () => apply();
    window.addEventListener("resize", onResize);
    const settle = window.setTimeout(apply, 1500);

    return () => {
      disposed = true;
      window.clearTimeout(settle);
      window.removeEventListener("resize", onResize);
      observer.disconnect();
      const host = document.querySelector<HTMLElement>("[data-sg-design-surface]");
      if (host && host.hasAttribute(MARK)) {
        host.style.paddingBottom = host.getAttribute(MARK) || "";
        host.removeAttribute(MARK);
      }
    };
  }, []);

  return null;
}
